import { uniq } from 'lodash';

import type {
  GetOrgAdultsRequest,
  GetOrgAdultsResponse,
  GetOrgYouthsRequest,
  GetOrgYouthsResponse,
  GetUnitAdultsResponse,
  GetUnitOrdersResponse,
  GetUnitParentsResponse,
  GetUnitYouthsResponse,
  RosterParent,
} from '@appTypes/esb';
import { NewRosterParent } from '@appTypes/esb/organizations/v2/units/_orgGuid/parents';
import { parentsRosterAdapter } from '@modules/advancement/packRoster/utilsTyped';
import { featureFlags } from '@modules/featureFlags/utils/featureFlags';
import { tagTypes } from '@modules/rtk/constants';
import { esbApi } from '@modules/rtk/esb-api';
import tagFactory, { type TagType } from '@modules/rtk/utils/tagFactory';

import {
  type CreateSubUnitWithMembersArg,
  type DeleteSubUnitWithMembersArg,
  type SubUnitSaveResult,
  type UpdateSubUnitWithMembersArg,
  runCreateSubUnitWithMembers,
  runDeleteSubUnitWithMembers,
  runUpdateSubUnitWithMembers,
} from './subUnitMutations';

/**
 * Cleared by the combined sub-unit mutations on every outcome: writes may have
 * applied before a failure, and a failed create may have created a sub-unit
 * whose response was lost. The sub-unit list only when it may have changed
 * (create, rename, delete). Den chief changes also clear what
 * `removeDenChief` clears for those users.
 *
 * Of the unit roster, only the youths and adults: their `positions` carry the
 * den/patrol membership the old roster, messaging and the den chief records
 * read. Sub-unit writes never change the parents, so they aren't refetched.
 */
export const subUnitWithMembersTags = (
  orgGuid: string,
  denChiefUserIds: Array<number | undefined>,
  listChanged: boolean,
): TagType[] => {
  const tags: TagType[] = [
    tagFactory.SubUnits.forOrg(orgGuid),
    tagFactory.UnitRoster.youthRoster(orgGuid),
    tagFactory.UnitRoster.adultRoster(orgGuid),
    ...(listChanged ? [tagFactory.SubUnits.subUnits(orgGuid)] : []),
  ];
  if (denChiefUserIds.length === 0) return tags;

  const userIds = uniq(
    denChiefUserIds.filter((id): id is number => id !== undefined),
  );

  return [
    ...tags,
    tagFactory.UnitPendingLeadershipPositions.forOrg(orgGuid),
    ...userIds.map(tagFactory.YouthLeadershipPositions.forUser),
  ];
};

export const rosterApi = esbApi.injectEndpoints({
  /**
   * @esbEndpoint GET /organizations/v2/:organizationGuid/orders
   */
  endpoints: builder => ({
    getUnitOrders: builder.query<
      GetUnitOrdersResponse,
      {
        organizationGuid: string;
      }
    >({
      query: arg => ({
        url: `/organizations/v2/${arg.organizationGuid}/orders`,
        method: 'GET',
      }),
      providesTags: (_result, error, arg) => {
        if (error) return [];

        return [tagFactory.UnitRenewalOrders.orgOrders(arg.organizationGuid)];
      },
    }),

    /**
     * @esbEndpoint GET /organizations/v2/units/:organizationGuid/youths
     */
    getUnitRosterYouths: builder.query<
      GetUnitYouthsResponse,
      {
        organizationGuid: string;
      }
    >({
      query: arg => ({
        url: `/organizations/v2/units/${arg.organizationGuid}/youths`,
        method: 'GET',
      }),
      providesTags: (_result, error, arg) => {
        if (error) return [];
        const tags = [
          // useful to reset all rosters for a unnit
          tagFactory.UnitRoster.organizationRosters(arg.organizationGuid),
          tagFactory.UnitRoster.youthRoster(arg.organizationGuid),
        ];

        if (_result?.users) {
          // useful to refresh roster based on a particular youth update
          tags.push(
            ..._result.users.map(user => ({
              id: `RosterYouth-${user.personGuid}`,
              type: tagTypes.UnitRoster,
            })),
          );
        }
        return tags;
      },
    }),

    /**
     * @esbEndpoint GET /organizations/v2/units/:organizationGuid/adults
     */
    getUnitRosterAdults: builder.query<
      GetUnitAdultsResponse,
      {
        organizationGuid: string;
      }
    >({
      query: arg => ({
        url: `/organizations/v2/units/${arg.organizationGuid}/adults`,
        method: 'GET',
      }),
      providesTags: (_result, error, arg) => {
        if (error) return [];

        const tags = [
          {
            // useful to reset all rosters for a unnit
            id: arg.organizationGuid,
            type: tagTypes.UnitRoster,
          },
          {
            id: `Adults-${arg.organizationGuid}`,
            type: tagTypes.UnitRoster,
          },
        ];

        if (_result?.users) {
          // useful to refresh roster based on a particular adult update
          tags.push(
            ..._result.users.map(user => ({
              id: `RosterAdult-${user.personGuid}`,
              type: tagTypes.UnitRoster,
            })),
          );
        }

        return tags;
      },
    }),

    /**
     * @esbEndpoint POST /organizations/v2/:organizationGuid/orgYouths
     */
    getOrgYouths: builder.query<
      GetOrgYouthsResponse,
      {
        organizationGuid: string;
        body: GetOrgYouthsRequest;
      }
    >({
      query: arg => ({
        url: `/organizations/v2/${arg.organizationGuid}/orgYouths`,
        method: 'POST',
        body: arg.body,
      }),
      providesTags: (_result, error, arg) => {
        if (error) return [];
        const youthGuids =
          _result?.members
            ?.map(member => member.personGuid)
            .map(guid => tagFactory.OrganizationRoster.youthInRoster(guid)) ||
          [];
        return [
          tagFactory.OrganizationRoster.organizationRosters(
            arg.organizationGuid,
          ),
          tagFactory.OrganizationRoster.youthRoster(arg.organizationGuid),
          // useful to reset the whole roster if we know a youth has changed
          ...youthGuids,
        ];
      },
    }),

    /**
     * @esbEndpoint POST /organizations/v2/:organizationGuid/orgAdults
     */
    getOrgAdults: builder.query<
      GetOrgAdultsResponse,
      {
        organizationGuid: string;
        body: GetOrgAdultsRequest;
      }
    >({
      query: arg => ({
        url: `/organizations/v2/${arg.organizationGuid}/orgAdults`,
        method: 'POST',
        body: arg.body,
      }),
      providesTags: (_result, error, arg) => {
        if (error) return [];
        const personGuidsTags =
          _result?.members
            ?.map(member => member.personGuid)
            .map(guid => tagFactory.OrganizationRoster.personInRoster(guid)) ||
          [];
        return [
          tagFactory.OrganizationRoster.organizationRosters(
            arg.organizationGuid,
          ),
          tagFactory.OrganizationRoster.adultRoster(arg.organizationGuid),
          // useful to reset the whole roster if we know an adult has changed
          ...personGuidsTags,
        ];
      },
    }),

    /**
     * same as POST /organizations/v2/:organizationGuid/orgAdults
     * but multiple requests
     */
    getMultipleOrgAdults: builder.query<
      GetOrgAdultsResponse[],
      {
        orgGuids: string[];
        body: GetOrgAdultsRequest;
      }
    >({
      queryFn: async (arg, _queryApi, _extraOptions, baseQuery) => {
        const requests = arg.orgGuids.map(orgGuid =>
          baseQuery({
            url: `/organizations/v2/${orgGuid}/orgAdults`,
            method: 'POST',
            body: arg.body,
          }),
        );

        const responses = await Promise.all(requests);
        const datas = responses
          .filter(item => !!item.data)
          .map(item => item.data) as GetOrgAdultsResponse[];
        return { data: datas };
      },
      providesTags: (result, error, arg) => {
        if (!result || error) return [];

        return arg.orgGuids.map(orgGuid => ({
          id: `OrgAdults-${orgGuid}`,
          type: tagTypes.OrganizationRoster,
        }));
      },
    }),

    /**
     * same as POST /organizations/v2/:organizationGuid/orgYouths
     * but multiple requests
     */
    getMultipleOrgYouths: builder.query<
      GetOrgYouthsResponse[],
      {
        orgGuids: string[];
        body: GetOrgYouthsRequest;
      }
    >({
      queryFn: async (arg, _queryApi, _extraOptions, baseQuery) => {
        const requests = arg.orgGuids.map(orgGuid =>
          baseQuery({
            url: `/organizations/v2/${orgGuid}/orgYouths`,
            method: 'POST',
            body: arg.body,
          }),
        );

        const responses = await Promise.all(requests);
        const datas = responses
          .filter(item => !!item.data)
          .map(item => item.data) as GetOrgYouthsResponse[];
        return { data: datas };
      },
      providesTags: (result, error, arg) => {
        if (!result || error) return [];

        return arg.orgGuids.map(orgGuid => ({
          id: `OrgYouths-${orgGuid}`,
          type: tagTypes.OrganizationRoster,
        }));
      },
    }),

    /**
     * @esbEndpoint GET /organizations/v2/units/:organizationGuid/parents
     */
    getUnitRosterParents: builder.query<
      RosterParent[],
      {
        organizationGuid: string;
      }
    >({
      query: arg => ({
        url: `/organizations/v2/units/${arg.organizationGuid}/parents`,
        method: 'GET',
      }),
      transformResponse: (result: GetUnitParentsResponse): RosterParent[] => {
        if (featureFlags.getFlag('SBL_7035_NEW_PARENT_RESPONSE')) {
          return parentsRosterAdapter(result as NewRosterParent[]);
        }

        return result as RosterParent[];
      },
      providesTags: (_result, error, arg) => {
        if (error) return [];
        return [
          {
            // useful to reset all rosters for a unnit
            id: arg.organizationGuid,
            type: tagTypes.UnitRoster,
          },
          {
            id: `Parents-${arg.organizationGuid}`,
            type: tagTypes.UnitRoster,
          },
        ];
      },
    }),

    /**
     * Creates the sub-unit, then adds its youths, adults and den chiefs in
     * parallel. When every add fails, it deletes the new, empty sub-unit and
     * returns the first add error; if that delete fails too, the sub-unit is
     * returned as a partial save instead. `failed` lists the steps that failed.
     *
     * @esbEndpoint POST /organizations/v2/units/:organizationGuid/:subUnitType/add
     * @esbEndpoint POST /organizations/v2/units/:organizationGuid/:subUnitType/:subUnitId/youths
     * @esbEndpoint POST /organizations/v2/units/:organizationGuid/:subUnitType/:subUnitId/adults
     * @esbEndpoint POST /organizations/v2/:organizationGuid/denChief/add
     * @esbEndpoint DELETE /organizations/v2/units/:organizationGuid/:subUnitType/:subUnitId
     */
    createSubUnitWithMembers: builder.mutation<
      SubUnitSaveResult,
      CreateSubUnitWithMembersArg
    >({
      queryFn: (arg, _api, _opts, baseQuery) =>
        runCreateSubUnitWithMembers(arg, baseQuery),
      invalidatesTags: (_res, _error, arg) =>
        subUnitWithMembersTags(
          arg.orgGuid,
          arg.denChiefs.map(({ userId }) => userId),
          true,
        ),
    }),

    /**
     * Sends, in parallel, only the parts that have work: rename, new youths
     * and adults, den chiefs added and removed, and removed youths and adults.
     * Always resolves; `failed` lists the steps that failed. Den chief calls
     * get `denId: [subUnitId]`, as in create.
     *
     * @esbEndpoint PUT /organizations/v2/units/:organizationGuid/:subUnitType/:subUnitId
     * @esbEndpoint POST /organizations/v2/units/:organizationGuid/:subUnitType/:subUnitId/youths
     * @esbEndpoint POST /organizations/v2/units/:organizationGuid/:subUnitType/:subUnitId/adults
     * @esbEndpoint POST /organizations/v2/:organizationGuid/denChief/add
     * @esbEndpoint POST /organizations/v2/:organizationGuid/denChief/remove
     * @esbEndpoint DELETE /organizations/v2/units/:organizationGuid/:subUnitType/:subUnitId/users
     */
    updateSubUnitWithMembers: builder.mutation<
      SubUnitSaveResult,
      UpdateSubUnitWithMembersArg
    >({
      queryFn: (arg, _api, _opts, baseQuery) =>
        runUpdateSubUnitWithMembers(arg, baseQuery),
      invalidatesTags: (_res, _error, arg) =>
        subUnitWithMembersTags(
          arg.orgGuid,
          [...arg.addDenChiefs, ...arg.removeDenChiefs].map(
            ({ userId }) => userId,
          ),
          !!arg.rename,
        ),
    }),

    /**
     * Removes the youths, adults and den chiefs, then deletes the sub-unit,
     * so the backend only ever deletes an empty one (SBL-6710). On failure the
     * error is the failed call's own. A retry passes empty lists and only runs
     * the delete.
     *
     * @esbEndpoint DELETE /organizations/v2/units/:organizationGuid/:subUnitType/:subUnitId/users
     * @esbEndpoint POST /organizations/v2/:organizationGuid/denChief/remove
     * @esbEndpoint DELETE /organizations/v2/units/:organizationGuid/:subUnitType/:subUnitId
     */
    deleteSubUnitWithMembers: builder.mutation<
      void,
      DeleteSubUnitWithMembersArg
    >({
      queryFn: (arg, _api, _opts, baseQuery) =>
        runDeleteSubUnitWithMembers(arg, baseQuery),
      invalidatesTags: (_res, _error, arg) =>
        subUnitWithMembersTags(
          arg.orgGuid,
          arg.denChiefs.map(({ userId }) => userId),
          true,
        ),
    }),
  }),
});

export const {
  useGetUnitOrdersQuery,
  useLazyGetUnitOrdersQuery,
  useGetUnitRosterAdultsQuery,
  useGetUnitRosterYouthsQuery,
  useGetUnitRosterParentsQuery,
  useLazyGetUnitRosterAdultsQuery,
  useLazyGetUnitRosterYouthsQuery,
  useLazyGetUnitRosterParentsQuery,
  useGetOrgYouthsQuery,
  useLazyGetOrgYouthsQuery,
  useGetOrgAdultsQuery,
  useLazyGetOrgAdultsQuery,
  useGetMultipleOrgAdultsQuery,
  useGetMultipleOrgYouthsQuery,
  useCreateSubUnitWithMembersMutation,
  useUpdateSubUnitWithMembersMutation,
  useDeleteSubUnitWithMembersMutation,
} = rosterApi;
